/**
 * Upshift public vault listing.
 *
 * A single un-paginated REST endpoint returns every Upshift vault across
 * all chains (EVM and non-EVM) in one response. We fetch it once and
 * filter to the requested EVM chain in `fetchPublic`. Pricing and yield
 * come straight from the API, so the public-data fetch needs no multicall
 * and no archival RPC.
 *
 * Endpoint (no auth):
 *   https://app.upshift.finance/api/proxy/vaults
 */

export const UPSHIFT_VAULTS_URL = 'https://app.upshift.finance/api/proxy/vaults'

/**
 * EVM chains where Upshift has visible vaults. Numeric chain ids as
 * strings. The endpoint is global (returns every chain regardless), so
 * this set only short-circuits the round-trip on chains Upshift doesn't
 * cover. Extend as Upshift deploys to more chains — the parser is
 * chain-agnostic.
 */
export const UPSHIFT_CHAIN_IDS = new Set<string>([
  '1', // Ethereum
  '14', // Flare
  '56', // BNB Chain
  '143', // Monad
  '43114', // Avalanche
  '8453', // Base
  '9745', // Plasma
  '57073', // Ink
  '999', // HyperEVM
  // Citrea. Added 2026-08-19: the listing carries a live, visible $2.56M
  // RockawayX vault there (`Earn ctUSD`) that this gate was short-circuiting
  // away — the fetch itself is HTTP-only and chain-agnostic, so a chain
  // missing from this set is the only thing that can hide one.
  '4114',
])

export const hasUpshiftVaults = (chainId: string): boolean =>
  UPSHIFT_CHAIN_IDS.has(String(chainId))

/** A `depositAssets[]` entry — an accepted underlying token. */
export interface UpshiftApiAsset {
  address: string | null
  symbol: string | null
  decimals: number | null
}

/** An amount the API reports in both scales. `raw` is ASSET-decimal scaled. */
export interface UpshiftApiAmount {
  normalized: string | null
  raw: string | null
}

/**
 * A `strategists[]` entry — **the curator**.
 *
 * Upshift is a curator PLATFORM, not a single operator: 26 visible vaults on
 * the chains we serve are run by 13 different firms. `name` is the only usable
 * handle — `address` is the literal string `"0x"` on every vault in the
 * listing, so there is nothing to key a curator registry on.
 *
 * Load-bearing because the vault NAME is not a substitute: half the book
 * (13 of 26 rows, $137.6M) is named after a brand that is NOT its curator —
 * "Tori Ecosystem Vault" is run by RockawayX, "Kelp Gain" by K3 Capital,
 * "Hyperbeat Ultra HYPE" by UltraYield. Dropping this field printed all of
 * them under the platform brand, which is the same misattribution that put a
 * SwissBorg vault under Gauntlet's name on the Morpho surface.
 */
export interface UpshiftApiStrategist {
  name: string | null
  logo: string | null
  address: string | null
  type: string | null
  website_url: string | null
}

/**
 * The `fees` block.
 *
 * **Read the waiver flags, never the bare rate.** Five of the vaults we serve
 * publish a non-zero `performance` alongside `isPerformanceWaived: true` — the
 * headline rate is the schedule the curator MAY charge, not what is charged
 * today. The `…WaivedUntilDate` / `…WaivedUntilTvl` companions bound the
 * waiver; both are null or 0 across the current book, so they are carried
 * through but not yet acted on.
 *
 * Both rates are PERCENT (`10` = 10 %), matching the package convention.
 */
export interface UpshiftApiFees {
  performance: number | null
  management: number | null
  isPerformanceWaived: boolean | null
  isManagementWaived: boolean | null
  performanceFeeWaivedUntilDate: string | null
  performanceFeeWaivedUntilTvl: number | null
  managementFeeWaivedUntilDate: string | null
  managementFeeWaivedUntilTvl: number | null
}

/**
 * `instant_redeem_config` — the optional immediate exit, present on one vault
 * today (Upshift Clear RWA). Null on every other row, which is why the
 * provider default stays `request-based`.
 */
export interface UpshiftApiInstantRedeem {
  subaccountAddress: string | null
  outputAssetSymbol: string | null
  redeemableAssets:
    | ({ symbol: string | null; spreadBps: number | null } | null)[]
    | null
  isPaused: boolean | null
  availableLiquidity: number | null
}

/** Shape of a single `data[]` entry in the API response. */
export interface UpshiftApiVault {
  chainId: number | null
  address: string | null
  name: string | null
  decimals: number | null
  status: string | null
  isVisible: boolean | null
  isDepositPaused: boolean | null
  isWithdrawalPaused: boolean | null
  apy: {
    apy: number | null
    campaignApy: number | null
    pointsApy: number | null
    underlyingApy: number | null
  } | null
  depositAssets: (UpshiftApiAsset | null)[] | null
  totalAssets: UpshiftApiAmount | null
  totalSupply: UpshiftApiAmount | null
  receipt: {
    symbol: string | null
    address: string | null
    decimals: number | null
  } | null
  latest_reported_tvl: number | null

  /** The curator — see {@link UpshiftApiStrategist}. One entry per vault. */
  strategists: (UpshiftApiStrategist | null)[] | null
  /** Curator cut — see {@link UpshiftApiFees}. */
  fees: UpshiftApiFees | null
  /**
   * Redemption delay in SECONDS. Varies 0 → 2,592,000 (30 days, both NEMO
   * vaults) across the book, so a single provider-level exit mode describes
   * none of them: without this a 30-day lock and a same-day exit render
   * identically.
   */
  lagDuration: number | null
  /**
   * The vault is in WIND-DOWN — withdrawals only, deposits are pointless even
   * though `isDepositPaused` is false. True on Hyperbeat Ultra HYPE today.
   * Distinct from a pause, which is temporary by implication.
   */
  withdrawalOnly: boolean | null
  /**
   * Total deposit cap, asset-scaled. Present on the `evm-2` generation; the
   * older `evm-0`/`evm-1` vaults leave it null and cap via `maxSupply`
   * instead.
   */
  depositCap: UpshiftApiAmount | null
  /** Per-deposit maximum on the older generations. NOT a vault-level cap —
   *  deliberately unused for capacity, which needs the total. */
  maxDepositAmount: UpshiftApiAmount | null
  /** Vault-level cap on the older generations; a `1e12` sentinel meaning
   *  "uncapped" wherever `depositCap` carries the real number. */
  maxSupply: UpshiftApiAmount | null
  /** Optional immediate exit — see {@link UpshiftApiInstantRedeem}. */
  instant_redeem_config: UpshiftApiInstantRedeem | null
  /** Contract generation: `evm-0` | `evm-1` | `evm-2`. */
  version: string | null
  /** Curator-authored explainer. */
  description: string | null
}

/**
 * Fetch the global Upshift vault listing. Returns the raw `data[]` array;
 * chain filtering and normalization to `UpshiftVault` happen in
 * `fetchPublic`.
 */
export async function fetchUpshiftApiVaults(): Promise<UpshiftApiVault[]> {
  const response = await fetch(UPSHIFT_VAULTS_URL, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    throw new Error(
      `Upshift vaults API failed: ${response.status} - ${response.statusText}`,
    )
  }
  const json: any = await response.json()
  return (json?.data ?? []) as UpshiftApiVault[]
}
